import { ISalesforceObjectMapping, SalesforceDataSourceApiService } from 'datasource-service-lib';
import { IEntity, INamedEntity } from 'interfaces-lib';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import {
  ISimpleTableColumn,
  ISimpleTableRow,
  SimpleTableSortFunctions,
} from '../../../common-components/simple-table/simple-table.interfaces';
import { IEntitySelectDialogDisplayTerms, IEntitySelectDialogService } from './ientityselectdialogservice';

/*
Example flow:
You launch Doc Client from a Contact.
The feature flag allows related templates, so the template list includes TR1__Closing_Report__c templates.
When the user clicks a Closing Report template, the app must ask:
Which Closing Report related to this Contact do you want to use?
This service powers that dialog.
 */
export class SalesforceEntitySelectDialogService implements IEntitySelectDialogService {
  constructor(private dataSourceApiService: SalesforceDataSourceApiService) {}

  getDisplayTerms(entity: IEntity, targetEntityType?: string): Observable<IEntitySelectDialogDisplayTerms> {
    const listEntityType = targetEntityType ?? entity.type;
    return this.dataSourceApiService.getEntityMappings().pipe(
      map((mappings) => {
        const label = this.findMapping(mappings, listEntityType)?.label?.trim();
        return {
          listEntityType,
          listEntityLabel: label || listEntityType,
        };
      }),
      distinctUntilChanged((previous, current) =>
        previous.listEntityType === current.listEntityType && previous.listEntityLabel === current.listEntityLabel
      )
    );
  }

  getDisplayColumns(): ISimpleTableColumn[] {
    return [
      {
        title: 'ID',
        width: '30%',
        sortFunction: SimpleTableSortFunctions.sortByTextField('id'),
      },
      {
        title: 'Name',
        width: '70%',
        sortFunction: SimpleTableSortFunctions.sortByTextField('name'),
      },
    ];
  }

  getRows(entity: IEntity, targetEntityType?: string): Observable<ISimpleTableRow[]> {
    if (!targetEntityType) {
      return of([]);
    }

    return this.dataSourceApiService.getEntityMappings().pipe(
      take(1),
      switchMap((mappings) => {
        const targetMapping = this.findMapping(mappings, targetEntityType);
        const relation = (targetMapping?.relatedObjects ?? []).find(
          (related) => related.objectName.toLowerCase() === entity.type.toLowerCase()
        );
        if (!relation) {
          return of([]);
        }

        return this.dataSourceApiService.list({
          entity: { type: targetMapping.objectName },
          fields: 'Id,Name',
          query: `${relation.relationSegment}.Id = '${this.escapeSoqlStringLiteral(entity.id)}'`,
          orderBy: 'Name',
        }).pipe(
          map((records) => records.map((record) => this.buildTableRow({
            id: record.Id ?? record.id,
            type: targetMapping.objectName,
            name: record.Name ?? record.name ?? record.Id ?? record.id,
          })))
        );
      })
    );
  }

  private findMapping(mappings: ISalesforceObjectMapping[] | null | undefined, objectName: string): ISalesforceObjectMapping | undefined {
    return (mappings ?? []).find((mapping) => mapping.objectName?.toLowerCase() === objectName.toLowerCase());
  }

  private escapeSoqlStringLiteral(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  }

  private buildTableRow(entityInfo: INamedEntity): ISimpleTableRow {
    return {
      cells: [{ text: entityInfo.id }, { text: entityInfo.name }],
      data: entityInfo,
    };
  }
}
